import { metersBetween } from '../pages/checkIn';
import { coordinatesOf, formatCoordinates, type GeoPoint } from '../records/coordinates';

/**
 * 🔒 Existing-equipment photos on a Deal's Need (piece 19; piece 35; decision
 * 5440; row T2743). A photo's kind, note and position are its own metadata,
 * set at the shot and kept on its `File`: `photoKind?`, `description?`,
 * `latitude?` and `longitude?`. The server writes them into the stored image's
 * EXIF and XMP too, so they travel with the file in the Deal Pack. 🔨 No tag
 * row is applied by hand; the kinds list is only what a camera tile offers.
 */

/** The Organization setting holding the kinds a camera tile offers, a list of words. */
export const EQUIPMENT_PHOTO_KINDS_KEY = 'deals.equipmentPhotoKinds';

/** 🔒 The kinds the install seeds, in the owner's words of 2026-10-17 and the brief's order. */
export const EQUIPMENT_PHOTO_KIND_SEED: readonly string[] = ['Disconnect', 'Feeder Panel', 'Pump 1', 'Pump 2', 'Panel Overview', 'Electrical Drawing', 'User Manual', 'Nameplate', 'Other'];

/** The setting's seed row, read by the install's seed beside the other pieces' rows. */
export const EQUIPMENT_PHOTO_SETTINGS: readonly { key: string; value: string[] }[] = [{ key: EQUIPMENT_PHOTO_KINDS_KEY, value: [...EQUIPMENT_PHOTO_KIND_SEED] }];

/** 🔒 Photos within this many meters of each other draw as one group, one unit of equipment (decision 5440). */
export const EQUIPMENT_PHOTO_GROUP_METERS = 15;

/** The longest kind a photo carries, typed or picked. */
export const PHOTO_KIND_MAX_LENGTH = 60;

/** A kind as stored: trimmed, with its inner runs of space folded to one; empty is none. */
export function cleanPhotoKind(kind: unknown): string | null {
  if (typeof kind !== 'string') return null;
  const clean = kind.trim().replace(/\s+/g, ' ');
  return clean === '' ? null : clean;
}

/** What the upload refuses in a kind, or null where it is taken. */
export function photoKindProblem(kind: unknown): string | null {
  if (kind === undefined || kind === null || kind === '') return null;
  if (typeof kind !== 'string') return 'A photo’s kind is a word or two of text.';
  const clean = cleanPhotoKind(kind);
  if (clean !== null && clean.length > PHOTO_KIND_MAX_LENGTH) return `A photo’s kind is at most ${PHOTO_KIND_MAX_LENGTH} characters; this one is ${clean.length}.`;
  return null;
}

/** A position's half as a form sends it, a number or its text; null where it is absent or not a number. */
function numberOf(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string' || value.trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * 🔒 The position an upload carries (decision 5440): both halves inside the
 * globe's ranges, or none sent. One half alone, or a half out of range, is a
 * refusal in words; nothing sent is no position.
 */
export function photoPositionOf(latitude: unknown, longitude: unknown): { ok: true; point: GeoPoint | null } | { ok: false; problem: string } {
  const sent = (value: unknown) => value !== undefined && value !== null && value !== '';
  if (!sent(latitude) && !sent(longitude)) return { ok: true, point: null };
  const lat = numberOf(latitude);
  const lng = numberOf(longitude);
  if (lat === null || lng === null) return { ok: false, problem: 'A photo’s position names both its latitude and its longitude as numbers.' };
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return { ok: false, problem: 'A photo’s latitude is between -90 and 90 and its longitude between -180 and 180.' };
  return { ok: true, point: { latitude: lat, longitude: lng } };
}

/** 🔒 The kinds a tile offers: the setting's words, cleaned and once each; the seed where the setting holds none. */
export function equipmentPhotoKinds(held: unknown): string[] {
  if (!Array.isArray(held)) return [...EQUIPMENT_PHOTO_KIND_SEED];
  const kinds: string[] = [];
  for (const value of held) {
    const clean = cleanPhotoKind(value);
    if (clean !== null && !kinds.some((kind) => kind.toLowerCase() === clean.toLowerCase())) kinds.push(clean);
  }
  return kinds.length > 0 ? kinds : [...EQUIPMENT_PHOTO_KIND_SEED];
}

/** 🔒 The most kinds the list holds (decision 5440): a typed kind past it is kept on its photo and not added. */
export const EQUIPMENT_PHOTO_KINDS_MAX = 50;

/**
 * 🔒 A typed kind is kept in the list (decision 5440): the list with the kind
 * added at its end where no kind of the same words, in any case, stands; null
 * where it already does, or where the list already holds
 * `EQUIPMENT_PHOTO_KINDS_MAX` kinds, so nothing is written.
 */
export function kindsWith(kinds: readonly string[], kind: string): string[] | null {
  const clean = cleanPhotoKind(kind);
  if (clean === null || kinds.some((held) => held.toLowerCase() === clean.toLowerCase())) return null;
  if (kinds.length >= EQUIPMENT_PHOTO_KINDS_MAX) return null;
  return [...kinds, clean];
}

/**
 * 🔒 What Settings refuses in the kinds list (decision 5440), or null where it is
 * taken: a list of words, each at most `PHOTO_KIND_MAX_LENGTH` characters, at
 * most `EQUIPMENT_PHOTO_KINDS_MAX` of them. A blank clears the row and the seed is offered.
 */
export function equipmentPhotoKindsProblem(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  if (!Array.isArray(value)) return 'The equipment photo kinds are a list of words.';
  if (value.some((kind) => typeof kind !== 'string')) return 'Each equipment photo kind is a word or two of text.';
  const long = value.find((kind: string) => (cleanPhotoKind(kind) ?? '').length > PHOTO_KIND_MAX_LENGTH);
  if (long !== undefined) return `A photo’s kind is at most ${PHOTO_KIND_MAX_LENGTH} characters; one is ${(cleanPhotoKind(long) ?? '').length}.`;
  if (value.length > EQUIPMENT_PHOTO_KINDS_MAX) return `The equipment photo kinds list holds at most ${EQUIPMENT_PHOTO_KINDS_MAX} kinds; this one holds ${value.length}.`;
  return null;
}

/** What a photo carries for its grouping: its point, where it has one. */
export interface PlacedPhoto {
  latitude?: number | null;
  longitude?: number | null;
}

/**
 * 🔒 The photos grouped by position (decision 5440): two photos within
 * `EQUIPMENT_PHOTO_GROUP_METERS` stand in one group, and a chain of close photos
 * is one group however far its ends are apart. A photo with no position stands
 * alone. The groups keep the order of their first photo, and each group its
 * photos' order.
 */
export function groupByPosition<T extends PlacedPhoto>(photos: readonly T[], meters: number = EQUIPMENT_PHOTO_GROUP_METERS): T[][] {
  const parent = photos.map((_, index) => index);
  const root = (index: number): number => {
    let at = index;
    while (parent[at] !== at) at = parent[at]!;
    return at;
  };
  const points = photos.map((photo) => coordinatesOf(photo.latitude, photo.longitude));
  for (let a = 0; a < photos.length; a += 1) {
    const pa = points[a];
    if (!pa) continue;
    for (let b = a + 1; b < photos.length; b += 1) {
      const pb = points[b];
      if (!pb || metersBetween(pa, pb) > meters) continue;
      const ra = root(a);
      const rb = root(b);
      if (ra !== rb) parent[Math.max(ra, rb)] = Math.min(ra, rb);
    }
  }
  const groups = new Map<number, T[]>();
  photos.forEach((photo, index) => {
    const key = root(index);
    groups.set(key, [...(groups.get(key) ?? []), photo]);
  });
  return [...groups.keys()].sort((a, b) => a - b).map((key) => groups.get(key)!);
}

/**
 * 🔒 The units the photos stand in, as the Need's grid, the Files tile and the Deal Pack number them (decision
 * 5440): each position group a unit numbered from 1 in the order the groups stand, then one group of the photos
 * with no position, numbered none.
 */
export function equipmentUnits<T extends PlacedPhoto>(photos: readonly T[], meters: number = EQUIPMENT_PHOTO_GROUP_METERS): { unit: number | null; photos: T[] }[] {
  const placed = groupByPosition(photos, meters).filter((group) => coordinatesOf(group[0]!.latitude, group[0]!.longitude) !== null);
  const unplaced = photos.filter((photo) => coordinatesOf(photo.latitude, photo.longitude) === null);
  return [...placed.map((group, index) => ({ unit: index + 1, photos: group })), ...(unplaced.length > 0 ? [{ unit: null, photos: unplaced }] : [])];
}

/** The position as a person reads it, six decimals each, or null where the photo has none. */
export function photoPositionLine(photo: PlacedPhoto): string | null {
  const point = coordinatesOf(photo.latitude, photo.longitude);
  return point ? formatCoordinates(point) : null;
}

/** Whether a file is an existing-equipment photo: an image carrying a kind. */
export function isEquipmentPhoto(file: { mimeType?: string | null; photoKind?: string | null }): boolean {
  return typeof file.mimeType === 'string' && file.mimeType.toLowerCase().startsWith('image/') && cleanPhotoKind(file.photoKind) !== null;
}

/** 🔒 T2922: whether the stream can answer `?size=thumb` for this content type, a raster picture; an SVG, a PDF or a sheet answers 404, so a tile asks no thumbnail for one. */
export function hasThumbnail(mimeType: string | null | undefined): boolean {
  const type = (mimeType ?? '').toLowerCase().split(';')[0]!.trim();
  return type.startsWith('image/') && type !== 'image/svg+xml';
}

/** 🔒 The extension a filed photo's name takes from its content type (decision 5461), so the name never lies about the bytes. */
const IMAGE_EXTENSIONS: Readonly<Record<string, string>> = { 'image/jpeg': 'jpg', 'image/jpg': 'jpg', 'image/pjpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp', 'image/heic': 'heic', 'image/heif': 'heic' };

/**
 * 🔒 The name a photo is filed under on upload (decision 5461; T2768): its labels, a running number and the
 * extension its content type names, such as `panel-design-main-panel-3.jpg` for a Discovery spot (the template's
 * key and the spot's tag) or `nameplate-2.jpg` for a kind on the Need. A type the map does not know takes the
 * extension the person's own file carried. Every label is lowered to letters, digits and hyphens, so the name is
 * the same in the Files tile, the pack's zip and any download.
 */
export function filedFileName(labels: readonly string[], number: number, original: string, mimeType?: string | null): string {
  const slug = (text: string) => text.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  const stem = labels.map(slug).filter((part) => part !== '').join('-') || 'photo';
  const extension = IMAGE_EXTENSIONS[(mimeType ?? '').trim().toLowerCase()] ?? /\.([A-Za-z0-9]{1,8})$/.exec(original.trim())?.[1]?.toLowerCase();
  return `${stem}-${number}${extension ? `.${extension}` : ''}`;
}
